import type { BaseQueryFn, BaseQueryApi } from '@reduxjs/toolkit/query';
import {
  fetchBaseQuery as rtkFetchBaseQuery,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query/react';
import fetch, { Headers, Request, Response } from 'cross-fetch';

import { envVar } from '~/constants';

const { PYTHON_API_PREFIX } = envVar();

/**
 * This is to prevent the warning:
 * Warning: `fetch` is not available. Please supply a custom `fetchFn` property to use
 * `fetchBaseQuery` on SSR environments.
 */
Object.assign(globalThis, { fetch, Headers, Request, Response });

export type FetchBaseQueryResult = BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  object,
  FetchBaseQueryMeta
>;

interface FetchBaseQueryArgs
  extends RequestInit,
    Pick<FetchArgs, 'responseHandler' | 'validateStatus' | 'timeout'> {
  baseUrl?: string;
  prepareHeaders?: (
    headers: Headers,
    api: Pick<
      BaseQueryApi,
      'getState' | 'extra' | 'endpoint' | 'type' | 'forced'
    >
  ) => Promise<Headers | void> | Headers | void;
  fetchFn?: (
    input: RequestInfo,
    init?: RequestInit | undefined
  ) => Promise<Response>;
  paramsSerializer?: (params: Record<string, any>) => string;
  /**
   * By default, we only check for 'application/json' and 'application/vnd.api+json' as the content-types for json. If you need to support another format, you can pass
   * in a predicate function for your given api to get the same automatic stringifying behavior
   * @example
   * ```ts
   * const isJsonContentType = (headers: Headers) => ["application/vnd.api+json", "application/json", "application/vnd.hal+json"].includes(headers.get("content-type")?.trim());
   * ```
   */
  isJsonContentType?: (headers: Headers) => boolean;
  /**
   * Defaults to `application/json`;
   */
  jsonContentType?: string;
}

export function fetchBaseQuery(
  input?: FetchBaseQueryArgs
): FetchBaseQueryResult {
  const { baseUrl, prepareHeaders, ...others } = input ?? {};

  return rtkFetchBaseQuery({
    baseUrl: `${PYTHON_API_PREFIX}${baseUrl ?? ''}`,
    ...others,
  });
}
